import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { Storefront, TrendUp, MapPin, WarningCircle } from "@phosphor-icons/react";

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-stone-200 bg-white p-4 text-center">
    <p className="font-heading text-2xl font-bold text-stone-900">{value}</p>
    <p className="text-xs text-stone-400">{label}</p>
  </div>
);

export default function AdminMarketplace() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/admin/marketplace").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <DashboardLayout nav={adminNav} title="Admin"><div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700" data-testid="marketplace-error">Couldn't load marketplace data. Please refresh or try again shortly.</div></DashboardLayout>;
  if (!d) return <DashboardLayout nav={adminNav} title="Admin"><div className="flex justify-center py-24"><Spinner /></div></DashboardLayout>;

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Hyderabad Marketplace</h1>
      <p className="mt-1 text-sm text-stone-500">Real supply vs demand across categories and locations. Use the gaps to decide which properties to onboard next.</p>

      <h2 className="mt-6 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><Storefront size={20} className="text-primary" /> Supply</h2>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Owners" value={d.supply.owners} />
        <Stat label="Approved properties" value={d.supply.approved_properties} />
        <Stat label="Verified" value={d.supply.verified_properties} />
        <Stat label="Total listed" value={d.supply.total_properties} />
        {Object.entries(d.supply.by_category).map(([c, v]) => <Stat key={c} label={c} value={v} />)}
      </div>

      <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><TrendUp size={20} className="text-primary" /> Demand</h2>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Customers" value={d.demand.customers} />
        <Stat label="Customer leads" value={d.demand.customer_leads} />
        <Stat label="Booking requests" value={d.demand.booking_requests} />
        <Stat label="Confirmed" value={d.demand.confirmed_bookings} />
      </div>
      <p className="mt-2 text-xs text-stone-400">Supply/demand ratio (properties per lead): {d.supply_demand_ratio ?? "—"}</p>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <p className="font-heading font-semibold">Top requested categories</p>
          <div className="mt-3 space-y-2">
            {d.demand.top_categories.length === 0 ? <p className="text-sm text-stone-400">No demand data yet.</p> :
              d.demand.top_categories.map((c) => <div key={c.category} className="flex justify-between text-sm"><span className="text-stone-700">{c.category}</span><span className="text-stone-500">{c.count} enquiries</span></div>)}
          </div>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <p className="font-heading font-semibold">Top requested locations</p>
          <div className="mt-3 space-y-2">
            {d.demand.top_locations.length === 0 ? <p className="text-sm text-stone-400">No demand data yet.</p> :
              d.demand.top_locations.map((c) => <div key={c.location} className="flex justify-between text-sm"><span className="text-stone-700">{c.location}</span><span className="text-stone-500">{c.count} enquiries</span></div>)}
          </div>
        </div>
      </div>

      <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><MapPin size={20} className="text-amber-500" /> Supply gaps & owner-acquisition priorities</h2>
      <p className="text-xs text-stone-400">Locations/categories where demand exceeds available inventory. Internal recommendation only.</p>
      <div className="mt-3 space-y-2" data-testid="supply-gaps">
        {d.supply_gaps.length === 0 ? <p className="rounded-xl border border-dashed border-stone-200 p-4 text-sm text-stone-400">No supply gaps detected yet.</p> :
          d.supply_gaps.map((g, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl border border-amber-100 bg-amber-50/50 px-4 py-3">
              <span className="text-sm font-medium text-stone-800">{g.category} in {g.location}</span>
              <span className="text-xs text-stone-500">Demand {g.demand} · Supply {g.supply} · <span className="font-semibold text-amber-700">onboard {g.gap} more</span></span>
            </div>
          ))}
      </div>

      <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><WarningCircle size={20} className="text-red-500" /> Unmatched requests</h2>
      <div className="mt-3" data-testid="unmatched-requests">
        {d.unmatched_requests.length === 0 ? (
          <EmptyState icon={WarningCircle} title="No unmatched requests" message="Enquiries with no matching approved inventory in their location will appear here." />
        ) : (
          <div className="space-y-2">
            {d.unmatched_requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-3">
                <div><p className="text-sm font-medium text-stone-800">{r.category} in {r.location}</p><p className="text-xs text-stone-400">{r.guests ? `${r.guests} guests` : ""}{r.budget ? ` · ₹${r.budget}` : ""}{r.date ? ` · ${r.date}` : ""}</p></div>
                <span className="text-xs text-stone-400">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
